import _ from 'lodash';
import { postListEntry } from 'Services';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { callAll, LIST_ENTRY_DATE_FORMAT, RQ_ADD_NEW_LIST_ENTRY, RQ_LIST_BY_ID } from 'Utils';
import useSingleList404Modal from 'Hooks/reduxHelperHooks/useSingleList404Modal';
import { AxiosResponse } from 'axios';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';
import { parseDateInTimeZone, timeZone } from '@brighterion/utils/date-timezone';
import { useTranslation } from 'react-i18next';

type HookArgs = {
    listId: number;
    mutationOptions?: Omit<MutationOptions<AxiosResponse<any>, any, any, any>, 'mutationKey'>;
};

export const transformListEntryData = (payload: any) => {
    let isoStartTime = '';

    if (payload?.startTime) {
        const utcStartTime = zonedTimeToUtc(payload.startTime, timeZone);
        isoStartTime = utcStartTime.toISOString();
    } else {
        const now = parseDateInTimeZone(new Date(), timeZone, LIST_ENTRY_DATE_FORMAT);
        const utcNow = zonedTimeToUtc(now, timeZone);
        isoStartTime = utcNow.toISOString();
    }

    if (payload?.endTime) {
        const utcEndTime = zonedTimeToUtc(payload.endTime, timeZone);
        const isoEndTime = utcEndTime.toISOString();

        return {
            ...payload,
            startTime: isoStartTime,
            endTime: isoEndTime
        };
    }

    return {
        ...payload,
        startTime: isoStartTime,
        // default to None, if no specific endTime value is passed
        endTime: null
    };
};

function useAddNewListEntry({ listId, mutationOptions }: HookArgs) {
    const { addTechnicalErrorModal } = useReduxModal();
    const { t } = useTranslation();
    const { listNotFound } = useSingleList404Modal();
    const queryClient = useQueryClient();
    const defaultSuccessHandler = () => {
        queryClient.invalidateQueries([RQ_LIST_BY_ID, listId]);
    };

    return useMutation([RQ_ADD_NEW_LIST_ENTRY], (values) => postListEntry(listId, transformListEntryData(values)), {
        ...mutationOptions,
        onSuccess: callAll(defaultSuccessHandler, mutationOptions?.onSuccess),
        onError: (error: any, variables, context) => {
            function handleDefault() {
                addTechnicalErrorModal();
            }

            // API returns multiple 404 in the array instead of network status code of 404
            const multiple404 =
                _.isArray(error.data) && error.data.some((e: { statusCode: number }) => e.statusCode === 404);

            if (multiple404) {
                listNotFound();
            } else {
                switch (error?.response?.status) {
                    case 409:
                        addTechnicalErrorModal('', t('Error adding list entry'), () => {}, {
                            status: 'error',
                            content: t('addListEntryDuplicateError')
                        });
                        break;
                    default:
                        handleDefault();
                        break;
                }
            }

            mutationOptions?.onError?.(error, variables, context);
        }
    });
}

export default useAddNewListEntry;
